import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { UserProfileDropdown } from '../components/UserProfileDropdown';
import { Sidebar } from '../components/Sidebar';

export const DashboardLayout: React.FC = () => {
  // Closed by default: below Tailwind's md breakpoint this is a fixed-position drawer that
  // overlays main content (md:relative md:translate-x-0 below force it open on desktop
  // regardless of this state). Defaulting to true made it permanently cover the page -- including
  // interactive controls like the appearance settings' Save button -- on every mobile viewport
  // load. Found via the mobile-chrome Playwright project (Pixel 5).
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useAuthStore();

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar onClose={() => setIsSidebarOpen(false)} />
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-30 cursor-default bg-black bg-opacity-50 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="border-b border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="text-gray-500 transition-colors hover:text-gray-700 focus:outline-none md:hidden"
            >
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Logo / Title */}
            <div className="flex-1 md:flex-none">
              <h1 className="text-xl font-bold text-gray-900 md:hidden">BookBed</h1>
            </div>

            {/* User Profile Dropdown */}
            <UserProfileDropdown user={{ id: user.id, name: user.name, email: user.email }} />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
